import * as React from "react";
import { type IColumn } from "@fluentui/react";
import { makeObservable, computed } from "mobx";
import { observer } from "mobx-react";
import { type UUID } from "../../utils/uuid";
import {
    type ShockEvent,
    ShockEventLevel,
    type ShockProfile,
    type User,
    type Vehicle,
    type VehicleGroup,
} from "../../api";
import { Severity, SeverityPill } from "./severity-pill";
import { defaultPageSize } from "../../utils/constants";

export interface ListShockEventItemCellProps {
    readonly shockEventId: UUID;
    readonly column: IColumn;
}

import { store } from "../../store";
import { MediumSafePill } from "./medium-safe-pill";

@observer
export class ListShockEventItemCell extends React.Component<ListShockEventItemCellProps> {
    constructor(props: ListShockEventItemCellProps) {
        super(props);
        makeObservable(this);
    }

    @computed private get shockEvent(): ShockEvent | undefined {
        return store.repositoryShockEvents.byId(this.props.shockEventId);
    }

    @computed private get vehicle(): Vehicle | undefined {
        if (!this.shockEvent) {
            return;
        }
        return store.repositoryVehicles.byId(this.shockEvent.vehicleId);
    }

    @computed private get shockProfile(): ShockProfile | undefined {
        if (!this.shockEvent?.shockProfileVersionId) {
            return;
        }

        return store.repositoryShockProfileVersions.byId(this.shockEvent.shockProfileVersionId);
    }

    @computed private get severity(): Severity | undefined {
        if (!this.shockEvent) {
            return;
        }

        switch (this.shockEvent.shockLevel) {
            case ShockEventLevel.LIGHT:
                return Severity.LIGHT;
            case ShockEventLevel.MEDIUM:
                return Severity.MEDIUM;
            case ShockEventLevel.SEVERE:
                return Severity.SEVERE;
        }
    }

    @computed private get user(): User | undefined {
        if (!this.shockEvent) {
            return;
        }

        return this.shockEvent.userId == null
            ? undefined
            : store.repositoryUsers.byId(this.shockEvent.userId);
    }

    @computed private get departmentLabel(): string | undefined {
        if (!this.vehicle || !this.vehicle.departmentId) {
            return;
        }
        return store.repositoryDepartments.byId(this.vehicle.departmentId)?.label;
    }

    @computed private get groups(): VehicleGroup[] {
        if (!this.vehicle) {
            return [];
        }
        return store.repositoryVehicleGroups.byQuery({
            pageSize: defaultPageSize,
            vehicleId: this.vehicle.id,
        });
    }

    public render(): JSX.Element {
        const { column } = this.props;
        if (!this.shockEvent) {
            return <></>;
        }
        switch (column.key) {
            case "time":
                return <span>{store.i18n.formatDateTime(this.shockEvent.time)}</span>;
            case "username":
                return this.user ? (
                    <span>{store.i18n.formatUserFullName(this.user)}</span>
                ) : (
                    <span />
                );

            case "department":
                return <span>{this.departmentLabel}</span>;
            case "vehicle":
                return this.vehicle ? <span>{this.vehicle.serialNumber}</span> : <span />;
            case "group":
                // TODO: This is a temporary hack until we know how to display all groups.
                // For now, we simply show the first group, if wone exists.
                return this.groups.length > 0 ? <span>{this.groups[0].label}</span> : <span />;
            case "shockProfile":
                return this.shockProfile ? (
                    <span>{store.i18n.formatShockProfile(this.shockProfile)}</span>
                ) : (
                    <span />
                );
            case "motionStatus":
                return this.shockEvent ? (
                    <span>
                        {store.i18n.formatShockEventMotionStatus(this.shockEvent.motionStatus)}
                    </span>
                ) : (
                    <span />
                );
            case "severity": {
                const mediumSafe = this.shockEvent?.mediumImpactSafe ?? null;
                const severityPill = this.severity ? (
                    <SeverityPill
                        severity={this.severity}
                        label={store.i18n.t(
                            `schema.shockEvent.shockLevel.${this.shockEvent.shockLevel}`,
                        )}
                    />
                ) : (
                    <span />
                );

                if (this.severity !== Severity.MEDIUM) {
                    return severityPill;
                }

                return (
                    <span>
                        {severityPill} {<MediumSafePill mediumSafe={mediumSafe} />}
                    </span>
                );
            }

            default:
                return <></>;
        }
    }
}
